// 业务逻辑里涉及公用方法
import router from 'umi/router';
import moment from 'moment';
import wx from 'weixin-js-sdk';
import { jsConfigAppBar, jsNavigationDelegate } from '@/utils/bridge';
import { showToast } from '@/utils/utils';
import { checkAndRouteToLoginAndUpdateStatus } from './routes';

/**
 * 给文本脱敏
 * @param {String} txt 需要脱敏的文本
 * @param {String} type 脱敏类型，默认3*4规则（ajk****ifdf）
 */
export function encryptText(txt, type = '3*4') {
  if (!txt) return;

  // return txt;
  const typeRule = {
    '3*4': [/^(.{3}).*(.{4})$/, '$1****$2'], // 示例: ajk****ifdf
    '3*6': [/^(.{3}).*(.{4})$/, '$1******$2'], // 示例: ajk******ifdf
    '1*1-dot': [/^(\d+\.).*(\.\d+)$/, '$1****$2'], // 示例:  226.****.99
    '1*1-colon': [/^([a-z|\d]+:).*(:[a-z|\d|\/\d]+)$/i, '$1****$2'], // 示例:  fa82:****:d1fc
  };
  const rule = typeRule[type];
  return txt.replace(rule[0], rule[1]);
}

/**
 * h5路由跳转
 * @param {string} pathname 跳转的站内h5路径
 * @param {boolean} replace 是否需要替换当前路由
 */
const handleJumpPage = (pathname, replace = false) => {
  jsNavigationDelegate({
    url: `${window.location.origin}${pathname}`,
  });
  if (replace) {
    router.replace(pathname);
  } else {
    router.push(pathname);
  }
};

/**
 * h5方式跳转页面，封装登录态校验逻辑
 * @param {string} pathname 跳转h5页面pathname，仅支持站内h5页面路径
 * @param {object} options 跳转页面配置
 * @param {boolean} options.needLogin 是否需要登录，如果需要则会先校验登录态，未登录则跳转到登录页
 * @param {boolean} options.replace 是否需要替换当前路由
 *
 */
export function jumpToPageByH5(
  pathname,
  options = { needLogin: false, replace: false, isMiniprogram: false }
) {
  const compositingOptions = { needLogin: false, replace: false, isMiniprogram: false, ...options };
  if (compositingOptions.needLogin) {
    // 校验登录态
    checkAndRouteToLoginAndUpdateStatus(() => handleJumpPage(pathname, compositingOptions.replace));
  } else {
    // 直接跳转
    if (compositingOptions.isMiniprogram) {
      if (pathname.includes('?')) {
        handleJumpPage(`${pathname}&comefrom=miniprogram`, compositingOptions.replace);
      } else {
        handleJumpPage(`${pathname}?comefrom=miniprogram`, compositingOptions.replace);
      }
    } else {
      handleJumpPage(pathname, compositingOptions.replace);
    }
  }
}

// 隐藏原生标题
export function hideNativeTitle() {
  jsConfigAppBar({
    style: 0,
  });
}

// 设置字体样式
export function setHtmlFontSize(styles) {
  const doc = document;
  doc.documentElement.setAttribute('class', styles['set-font']);
  doc.body.className = styles['set-font'];
}

// 设置body样式
export function setBodyStyle(style) {
  const doc = document;
  doc.documentElement.setAttribute('class', style);
  doc.body.className = style;
}

// 设置body透明度为0
export function setBodyOpacity(styles) {
  const doc = document;
  setBodyStyle(styles['body-opacity']);
  doc.getElementById('root').setAttribute('style', 'background: rgba(0, 0, 0, 0)');
}

// 处理数字
export function renderNum(num, decimals = false) {
  if (!num && num !== 0) return '--';
  return decimals ? Number(num).toFixed(2) : num;
}

export function throttle(fn, delay) {
  let valid = true;
  return function() {
    if (!valid) {
      return false;
    }
    // 工作时间，执行函数并且在间隔期内把状态位设为无效
    valid = false;
    setTimeout(() => {
      fn();
      valid = true;
    }, delay);
  };
}

/**
 * 当前版本curV是否大于等于待比较版本preV
 * @param {*} preV 待比较版本
 * @param {*} curV 当前版本
 * @returns 布尔值
 */
export const compareVersion = (preV, curV) => {
  if (!preV || !curV) return false;
  const preStrArray = preV.split('.'),
    curStrArray = curV.split('.');
  for (let i = 0; i < preStrArray.length; i++) {
    if (i >= curStrArray.length) return false;
    // if (Number(preStrArray[i]) > Number(curStrArray[i])) return false;
    if (Number(curStrArray[i]) > Number(preStrArray[i])) {
      return true;
    } else if (Number(preStrArray[i]) > Number(curStrArray[i])) {
      return false;
    }
  }
  return true;
};

/**
 * 滚动指定元素到页面顶部
 * @param {string} id DOM元素ID
 * @param {number} distance 距离页面顶部的距离
 */
export const scrollToElement = (id, distance = 42) => {
  const el = document.getElementById(id);
  if (el) {
    const scrollDistance = el.offsetTop - distance;
    document.documentElement.scrollTop = scrollDistance;
    document.body.scrollTop = scrollDistance;
  }
};

/**
 * 判断数据是否存在
 * @param {*} data
 * @returns boolean
 */
export const isExist = data => {
  return data !== null && data !== undefined && !Number.isNaN(data);
};

/**
 * 统一处理数字
 * @param {*} float 传入数字int/float
 * @param {*} precision 精度数
 * @returns 返回处理之后的小数
 */
export const renderFloat = (float, precision = 2) => {
  if (isExist(float) && float !== -1) {
    return Number(float).toFixed(precision);
  }
  return '--';
};

export const fixedBody = () => {
  const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
  document.body.style.cssText += 'position:fixed;width:100%;top:-' + scrollTop + 'px;';
};

export const looseBody = () => {
  const body = document.body;
  body.style.position = '';
  const top = body.style.top;
  document.body.scrollTop = document.documentElement.scrollTop = -parseInt(top);
  body.style.top = '';
};

/**
 *  判断给出的时间字符串是否是今天
 * @param {string} time 时间字符串
 * @param {string} 时间格式
 * @returns {boolean} true-是今天,false-不是今天
 */
export const isToady = (time, format = 'YYYY-MM-DD') => time === moment().format(format);

/**
 * 获取当前月份
 * @param {string} time 时间字符串
 * @returns {number} 当前时间字符串解析出来的月份
 */
export const getMonth = time => moment(time).month() + 1;

/**
 *
 * @param {string} curDate 当前日期
 * @param {string} compareDate 待比较的日期
 * @returns 是否是相同月份
 */
export const isSameMonth = (curDate, compareDate) => moment(curDate).isSame(compareDate, 'month');

// 获取手机号尾号
export const getTailFourNumber = phone => (phone ? phone.slice(-4) : '');

// 跳转到APP下载页面
export const jumpToPageByAppDown = () => {
  showToast('请到网心云APP查看领取');
  setTimeout(() => {
    window.location.href =
      'https://act2.walk-live.com/myFrient10/invite/x86/download?comefrom=miniprogram';
  }, 1000);
};

// // 跳转到APP下载页面
// export const jumpToPageByWeekActivity = () => {
//     window.location.href = 'https://act2.walk-live.com/acts/weekly-ranking';
// };

// 跳转到小程序，然后小程序跳转到官网商城
export const jumpToPageByMiniprogram = (comelink = '') => {
  // 跳转到有赞小程序
  console.log('jumpToPageByMiniprogram---comelink', comelink);
  wx.miniProgram.redirectTo({
    url: `/myPages/pages/transit/index?comefrom=h5box&comelink=${comelink}`,
  });
};

export const getProductPRIframeUrl = (url, comefrom) => {
  if (comefrom === 'miniprogram') {
    url += '&comefrom=miniprogram';
  }
  return url;
};

export const reducerDate = date => {
  return date.replace(/(\d{4})(\d{2})(\d{2})/, (match, $1, $2, $3) => [$1, $2, $3].join('-'));
};
